/**
 * UNIK Vidéos — Slider + Video Controls
 * Uses MinimogLibs.Swiper for the slider, IntersectionObserver for lazy play/pause,
 * and handles play/pause/mute/fullscreen per card.
 */
(function () {
  'use strict';

  const SELECTORS = {
    section: '.unik-videos',
    slider: '.unik-videos__slider',
    card: '.unik-videos__card',
    video: '.unik-videos__card video',
    poster: '.unik-videos__poster',
    btnPlay: '[data-uv-play]',
    btnMute: '[data-uv-mute]',
    btnFullscreen: '[data-uv-fullscreen]',
    progressBar: '.unik-videos__progress-bar',
    navPrev: '.unik-videos__nav-prev',
    navNext: '.unik-videos__nav-next',
    pagination: '.unik-videos__pagination',
  };

  /**
   * Wait for MinimogLibs.Swiper to be available (it may load async)
   */
  function waitForSwiper(callback, maxAttempts) {
    var attempts = 0;
    var max = maxAttempts || 50;
    var timer = setInterval(function () {
      attempts++;
      if (window.MinimogLibs && window.MinimogLibs.Swiper) {
        clearInterval(timer);
        callback();
      } else if (attempts >= max) {
        clearInterval(timer);
        console.warn('[UNIK Videos] MinimogLibs.Swiper not found after waiting.');
      }
    }, 100);
  }

  /**
   * Initialise one section instance
   */
  function initSection(sectionEl) {
    var autoplay = sectionEl.dataset.autoplay === 'true';
    var autoplaySpeed = parseInt(sectionEl.dataset.autoplaySpeed, 10) || 5000;
    var loop = sectionEl.dataset.loop === 'true';
    var showNav = sectionEl.dataset.showNav === 'true';
    var showPagination = sectionEl.dataset.showPagination === 'true';
    var slidesDesktop = parseInt(sectionEl.dataset.slidesDesktop, 10) || 4;
    var slidesTablet = parseInt(sectionEl.dataset.slidesTablet, 10) || 3;
    var slidesMobile = parseInt(sectionEl.dataset.slidesMobile, 10) || 1;
    var gap = parseInt(sectionEl.dataset.gap, 10) || 16;

    var swiperEl = sectionEl.querySelector(SELECTORS.slider);
    if (!swiperEl) return;

    // Build Swiper config
    var swiperConfig = {
      slidesPerView: slidesMobile,
      spaceBetween: gap,
      grabCursor: true,
      loop: loop,
      watchSlidesProgress: true,
      breakpoints: {
        480: { slidesPerView: Math.min(slidesMobile + 1, slidesTablet), spaceBetween: gap },
        768: { slidesPerView: slidesTablet, spaceBetween: gap },
        1024: { slidesPerView: slidesDesktop, spaceBetween: gap },
      },
    };

    if (autoplay) {
      swiperConfig.autoplay = {
        delay: autoplaySpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    if (showNav) {
      var prevBtn = sectionEl.querySelector(SELECTORS.navPrev);
      var nextBtn = sectionEl.querySelector(SELECTORS.navNext);
      if (prevBtn && nextBtn) {
        swiperConfig.navigation = {
          prevEl: prevBtn,
          nextEl: nextBtn,
        };
      }
    }

    if (showPagination) {
      var paginationEl = sectionEl.querySelector(SELECTORS.pagination);
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }
    }

    // Init Swiper via MinimogLibs
    var swiper = new MinimogLibs.Swiper(swiperEl, swiperConfig);

    // ---- Video controls per card ----
    var cards = sectionEl.querySelectorAll(SELECTORS.card);
    cards.forEach(function (card) { initCard(card); });

    // Intersection Observer: pause off-screen videos
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target.querySelector('video');
            if (!video) return;
            if (!entry.isIntersecting) {
              video.pause();
            }
          });
        },
        { threshold: 0.3 }
      );
      cards.forEach(function (card) { observer.observe(card); });
    }

    // Store reference for Shopify editor
    sectionEl._unikSwiper = swiper;
  }

  /**
   * Init controls for a single video card
   */
  function initCard(card) {
    var video = card.querySelector('video');
    if (!video) return;

    var poster = card.querySelector(SELECTORS.poster);
    var btnPlay = card.querySelector(SELECTORS.btnPlay);
    var btnMute = card.querySelector(SELECTORS.btnMute);
    var btnFullscreen = card.querySelector(SELECTORS.btnFullscreen);
    var progressBar = card.querySelector(SELECTORS.progressBar);

    // State
    var isPlaying = !video.paused;
    var isMuted = video.muted;
    var touchTimeout = null;

    // Initial icons
    updatePlayIcon(btnPlay, isPlaying);
    updateMuteIcon(btnMute, isMuted);

    // Poster click => play
    if (poster) {
      poster.addEventListener('click', function (e) {
        e.stopPropagation();
        poster.classList.add('is-hidden');
        video.play().then(function () {
          isPlaying = true;
          updatePlayIcon(btnPlay, isPlaying);
        }).catch(function () {});
      });
    }

    // Play / Pause
    if (btnPlay) {
      btnPlay.addEventListener('click', function (e) {
        e.stopPropagation();
        if (video.paused) {
          if (poster) poster.classList.add('is-hidden');
          video.play().then(function () {
            isPlaying = true;
            updatePlayIcon(btnPlay, isPlaying);
          }).catch(function () {});
        } else {
          video.pause();
          isPlaying = false;
          updatePlayIcon(btnPlay, isPlaying);
        }
      });
    }

    // Mute / Unmute
    if (btnMute) {
      btnMute.addEventListener('click', function (e) {
        e.stopPropagation();
        isMuted = !isMuted;
        video.muted = isMuted;
        updateMuteIcon(btnMute, isMuted);
      });
    }

    // Fullscreen
    if (btnFullscreen) {
      btnFullscreen.addEventListener('click', function (e) {
        e.stopPropagation();
        if (video.requestFullscreen) {
          video.requestFullscreen();
        } else if (video.webkitRequestFullscreen) {
          video.webkitRequestFullscreen();
        } else if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen(); // iOS
        }
      });
    }

    // Progress bar update
    if (progressBar) {
      video.addEventListener('timeupdate', function () {
        if (video.duration) {
          var pct = (video.currentTime / video.duration) * 100;
          progressBar.style.width = pct + '%';
        }
      });
    }

    // Video ended => reset
    video.addEventListener('ended', function () {
      isPlaying = false;
      updatePlayIcon(btnPlay, isPlaying);
      if (progressBar) progressBar.style.width = '0%';
      if (poster) poster.classList.remove('is-hidden');
    });

    // Mobile touch: toggle controls visibility
    card.addEventListener('touchstart', function () {
      card.classList.add('is-touched');
      clearTimeout(touchTimeout);
      touchTimeout = setTimeout(function () {
        card.classList.remove('is-touched');
      }, 3500);
    }, { passive: true });
  }

  // ---- Icon helpers ----
  function updatePlayIcon(btn, playing) {
    if (!btn) return;
    var icon = btn.querySelector('.uv-icon');
    var label = btn.querySelector('span');
    if (playing) {
      icon.innerHTML = '<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>';
      if (label) label.textContent = label.dataset.pause || 'Pause';
    } else {
      icon.innerHTML = '<polygon points="5,3 19,12 5,21"/>';
      if (label) label.textContent = label.dataset.play || 'Play';
    }
  }

  function updateMuteIcon(btn, muted) {
    if (!btn) return;
    var icon = btn.querySelector('.uv-icon');
    if (muted) {
      icon.innerHTML =
        '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
        '<line x1="23" y1="9" x2="17" y2="15"/>' +
        '<line x1="17" y1="9" x2="23" y2="15"/>';
    } else {
      icon.innerHTML =
        '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
        '<path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>' +
        '<path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>';
    }
  }

  // ---- Init on DOM ready, wait for Swiper ----
  function initAll() {
    waitForSwiper(function () {
      document.querySelectorAll(SELECTORS.section).forEach(initSection);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ---- Shopify Theme Editor support ----
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var section = e.target.querySelector(SELECTORS.section);
      if (section) {
        waitForSwiper(function () {
          initSection(section);
        });
      }
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var section = e.target.querySelector(SELECTORS.section);
      if (section && section._unikSwiper) {
        section._unikSwiper.destroy(true, true);
      }
    });
  }
})();
